import React, {useCallback, useState} from 'react'
import {Button} from '@vio/atlas/Button'
import {Flex} from '@vio/atlas/helpers/Flex'
import {InputText} from '@vio/atlas/InputText'

import {UploadStatus as UploadStatusType} from '../types/s3Upload'
import {generateCssThemeString} from '../utils'
import {uploadCssThemeS3} from '../utils/s3Upload'
import {UploadStatus} from './UploadStatus'

interface Props {
  basePalettes: Array<{group: string; shades: Record<string, string>}>
  semanticPalettes: Array<{group: string; referencePalette: string}>
}

/**
 * UploadSection component that encapsulates all upload-related logic and components
 */
export const UploadSection: React.FC<Props> = ({
  basePalettes,
  semanticPalettes
}) => {
  const [themeName, setThemeName] = useState<string>('')
  const [isThemeNameValid, setIsThemeNameValid] = useState<boolean>(false)
  const [uploadStatus, setUploadStatus] = useState<UploadStatusType>({
    isUploading: false,
    isSuccess: false,
    isError: false,
    message: ''
  })

  const handleThemeNameChange = useCallback(
    (value: string) => {
      setThemeName(value)
      // Validate theme name
      const trimmed = value.trim()
      const isValid = trimmed.length >= 2 && /^[a-zA-Z0-9_-]+$/.test(trimmed)
      setIsThemeNameValid(isValid)

      // Reset upload status when theme name changes
      if (uploadStatus.isSuccess || uploadStatus.isError) {
        setUploadStatus({
          isUploading: false,
          isSuccess: false,
          isError: false,
          message: ''
        })
      }
    },
    [uploadStatus.isSuccess, uploadStatus.isError]
  )

  const handleS3Upload = useCallback(async () => {
    if (!isThemeNameValid || !themeName.trim()) {
      setUploadStatus({
        isUploading: false,
        isSuccess: false,
        isError: true,
        message: 'Please enter a valid theme name before uploading'
      })
      return
    }

    // Start upload
    setUploadStatus({
      isUploading: true,
      isSuccess: false,
      isError: false,
      message: 'Uploading CSS theme to S3...'
    })

    try {
      const cssString = generateCssThemeString(basePalettes, semanticPalettes)
      const result = await uploadCssThemeS3({
        themeName: themeName.trim(),
        cssContent: cssString
      })

      setUploadStatus({
        isUploading: false,
        isSuccess: result.success,
        isError: !result.success,
        message: result.message,
        result: {
          success: result.success,
          message: result.message,
          themeOverrideUrl: result.data?.themeOverrideUrl,
          error: result.error
        }
      })
    } catch (error) {
      console.error('S3 upload error:', error)
      const errorMessage =
        error instanceof Error ? error.message : 'Upload failed'
      setUploadStatus({
        isUploading: false,
        isSuccess: false,
        isError: true,
        message: `Upload failed: ${errorMessage}`
      })
    }
  }, [themeName, isThemeNameValid, basePalettes, semanticPalettes])

  const handleCopyUrl = useCallback((url: string) => {
    if (navigator.clipboard) {
      navigator.clipboard
        .writeText(url)
        .then(() => {
          console.log('URL copied to clipboard:', url)
        })
        .catch(err => {
          console.error('Failed to copy URL:', err)
        })
    }
  }, [])

  return (
    <Flex.Column gap="s300" style={{marginTop: 24}}>
      <InputText
        id="theme-name"
        name="theme-name"
        value={themeName}
        onChange={handleThemeNameChange}
        placeholder="Enter theme name"
        caption={
          !isThemeNameValid && themeName.trim()
            ? 'Theme name must be 2-50 characters, alphanumeric, hyphens, and underscores only'
            : ''
        }
        isValid={isThemeNameValid}
      />

      <Button
        onClick={handleS3Upload}
        disabled={!isThemeNameValid || !themeName.trim()}
        loading={uploadStatus.isUploading}
      >
        {themeName ? `Upload ${themeName} Theme` : 'Upload Theme'}
      </Button>

      <UploadStatus uploadStatus={uploadStatus} onCopyUrl={handleCopyUrl} />
    </Flex.Column>
  )
}

export default UploadSection
